import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FileText,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Download,
} from "lucide-react";
import {
  bondsAPI,
  formatDate,
  formatCurrency,
  subscriptionAPI,
  filesAPI,
  api,
} from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import SafeText from "../../components/common/SafeText";
import { useAuth } from "../../contexts/AuthContext";
import { BOND_STATUS } from "../../constants/subscription.constants";

const ISIN_REGEX = /^[A-Z]{2}[A-Z0-9]{9}[0-9]$/;
const STOCK_CODE_REGEX = /^[A-Z]{2}[A-Z0-9]{6}$/;

// Document category labels for display (Issuer-uploaded documents only)
const DOCUMENT_CATEGORY_LABELS = {
  riskAssessment: "Risk Assessment",
  legalDocuments: "Legal Documents",
  covenantsDocuments: "Covenants Documents",
  valuationReport: "Valuation Report",
  shariahCertificate: "Shariah Certificate",
  securitiesLodgementForm: "Securities Lodgement Form",
  esgCertification: "ESG Certification",
  trustDeed: "Trust Deed",
  offeringCircular: "Offering Circular",
  prospectus: "Prospectus",
  termSheet: "Term Sheet",
};

// Document categories uploaded by investors (not issuer) - should be excluded
const INVESTOR_DOCUMENT_CATEGORIES = [
  "subAgreementSubmission",
  "locSubmission",
];

const isIssuerDocument = (doc) => {
  const category = doc?.metadata?.category;
  // Exclude investor-uploaded documents
  if (category && INVESTOR_DOCUMENT_CATEGORIES.includes(category)) {
    return false;
  }
  // Also check filename patterns for sub-agreements and LOCs
  const fileName = (doc?.name || doc?.originalName || "").toLowerCase();
  if (
    fileName.includes("sub_agreement") ||
    fileName.includes("subagreement") ||
    fileName.startsWith("loc_")
  ) {
    return false;
  }
  return true;
};

const getDocumentCategoryLabel = (doc) => {
  const category = doc?.metadata?.category;
  if (category && DOCUMENT_CATEGORY_LABELS[category]) {
    return DOCUMENT_CATEGORY_LABELS[category];
  }
  // Fallback: try to infer from filename
  const fileName = (doc?.name || doc?.originalName || "").toLowerCase();
  if (fileName.includes("risk")) return "Risk Assessment";
  if (fileName.includes("shariah")) return "Shariah Certificate";
  if (fileName.includes("valuation")) return "Valuation Report";
  if (fileName.includes("covenant")) return "Covenants Documents";
  if (fileName.includes("esg")) return "ESG Certification";
  if (fileName.includes("legal")) return "Legal Documents";
  if (fileName.includes("trust")) return "Trust Deed";
  if (fileName.includes("prospectus")) return "Prospectus";
  if (fileName.includes("term")) return "Term Sheet";
  return doc?.type === "image" ? "Image" : "Document";
};

const BondApplications = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const queryClient = useQueryClient();

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [arrangerNotes, setArrangerNotes] = useState("");
  const [approvalIsin, setApprovalIsin] = useState("");
  const [approvalIsinError, setApprovalIsinError] = useState("");
  const [approvalStockCode, setApprovalStockCode] = useState("");
  const [approvalStockCodeError, setApprovalStockCodeError] = useState("");

  // Filters
  const [bondTypeFilter, setBondTypeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch bond applications (only show pending review bonds that already have a subscription window set)
  const { data: applications, isPending: applicationsLoading } = useQuery({
    queryKey: ["bondApplications", bondTypeFilter],
    queryFn: () =>
      bondsAPI.getBonds({
        status: BOND_STATUS.PENDING_REVIEW, // Always filter for pending review only
        productType:
          bondTypeFilter !== "all"
            ? mapProductTypeToAPI(bondTypeFilter)
            : undefined,
        page: 1,
        limit: 100,
        sortBy: "createdAt",
        sortOrder: "desc",
      }),
    select: (data) => {
      // Handle the new response structure
      const responseData = data.data;
      if (responseData && responseData.success && responseData.data) {
        const bonds = responseData.data.bonds || [];
        // New requirement: only list bonds here AFTER subscription period has been set
        return bonds.filter(
          (bond) =>
            (bond.subscriptionStart || bond.subscriptionStartDate) &&
            (bond.subscriptionEnd || bond.subscriptionEndDate)
        );
      }
      return [];
    },
  });

  // Fetch documents for selected bond
  const { data: documentsData, isPending: documentsLoading } = useQuery({
    queryKey: ["bondDocuments", selectedApplication?.id],
    queryFn: () => bondsAPI.getBondDocuments(selectedApplication.id),
    enabled: !!selectedApplication?.id && showDetailModal,
    select: (data) => {
      const responseData = data.data;
      if (responseData && responseData.success && responseData.data) {
        return responseData.data.documents || [];
      }
      return [];
    },
  });

  // Helper function to map display status to API status
  const mapStatusToAPI = (displayStatus) => {
    const statusMap = {
      pending_review: BOND_STATUS.PENDING_REVIEW,
      approved: BOND_STATUS.APPROVED,
      rejected: BOND_STATUS.REJECTED,
      draft: BOND_STATUS.DRAFT,
    };
    return statusMap[displayStatus] || displayStatus;
  };

  // Helper function to map display product type to API product type
  const mapProductTypeToAPI = (displayType) => {
    const typeMap = {
      "Conventional Bonds": "Fixed Notes",
      Sukuk: "Sukuk",
    };
    return typeMap[displayType] || displayType;
  };

  // Helper function to map API status to display status
  const mapStatusToDisplay = (apiStatus) => {
    const statusMap = {
      draft: BOND_STATUS.DRAFT,
      pending_review: BOND_STATUS.PENDING_REVIEW,
      approved: BOND_STATUS.APPROVED,
      rejected: BOND_STATUS.REJECTED,
      listed: BOND_STATUS.LISTED,
      active: BOND_STATUS.ACTIVE,
      matured: BOND_STATUS.MATURED,
    };
    return statusMap[apiStatus] || apiStatus;
  };

  // Helper function to map API product type to display type
  const mapProductTypeToDisplay = (apiType) => {
    const typeMap = {
      "Fixed Notes": "Conventional Bonds",
      Sukuk: "Sukuk",
    };
    return typeMap[apiType] || apiType;
  };

  // Transform API data to match expected format
  const transformAPIData = (bonds) => {
    return bonds.map((bond) => ({
      id: bond._id,
      bondName: bond.bondName,
      issuer:
        bond.issuer?.corporateInfo?.companyName ||
        bond.issuer?.email ||
        "Unknown Issuer",
      productType: mapProductTypeToDisplay(bond.productType),
      nominalAmount: bond.issueAmount,
      submissionDate: bond.createdAt,
      status: mapStatusToDisplay(bond.status),
      couponRate: bond.couponRate,
      profitRate: bond.couponRate, // For Sukuk, we use couponRate as profitRate
      maturityDate: bond.maturityDate,
      issuanceDate: bond.issueDate,
      currency: bond.currency || "MYR",
      approvalDate: bond.approvedAt,
      documents: [
        { name: "Bond Application", type: "Application", status: "Submitted" },
        {
          name: "Issuer Documents",
          type: "Legal Documents",
          status: "Submitted",
        },
      ],
      // Additional fields from API
      bondNumber: bond.bondNumber,
      isin: bond.isin,
      stockCode: bond.stockCode,
      tokenDetails: bond.tokenDetails,
      fundingStatus: bond.fundingStatus,
      totalFunded: bond.totalFunded,
    }));
  };

  // Approve application mutation
  const approveMutation = useMutation({
    mutationFn: async ({ applicationId, isin, stockCode, notes }) => {
      // First approve the bond
      const approveResponse = await bondsAPI.approveBond(applicationId, {
        isin,
        stockCode,
        comments: notes || undefined,
      });

      // New flow: Bond should already be present in Subscription Management before this step.
      // Do not add/sync here to avoid duplicates.

      return approveResponse;
    },
    onSuccess: (data, variables) => {
      // Save approval to history
      saveApprovalHistory(
        "approve",
        selectedApplication,
        variables?.notes || "",
        null,
        {
          isin: variables?.isin,
          stockCode: variables?.stockCode,
        }
      );

      queryClient.invalidateQueries(["bondApplications"]);
      queryClient.invalidateQueries(["subscriptionBonds"]); // Invalidate subscription data
      setShowApproveModal(false);
      setSelectedApplication(null);
      setArrangerNotes("");
      setApprovalIsin("");
      setApprovalIsinError("");
      setApprovalStockCode("");
      setApprovalStockCodeError("");

      // Show success notification
      const bondName = selectedApplication?.bondName || "Bond";
      alert(
        `✅ Success!\n\n${bondName} has been approved and synced to the subscription management page.\n\nBond status has been changed to "Approved for Subscription" and can now proceed to the subscription phase.\n\nPlease go to the subscription management page to set the subscription period.`
      );
      console.log(
        `Bond application ${variables?.applicationId} approved and synced to Subscription Management`
      );
    },
    onError: (error) => {
      console.error("Approval error:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to approve bond application. Please try again.";
      alert(`❌ Error: ${errorMessage}`);
    },
  });

  // Function to save approval/rejection history
  const saveApprovalHistory = (
    action,
    application,
    notes,
    rejectionReason = null,
    extra = {}
  ) => {
    const historyEntry = {
      id: `${action}_${application.id}_${Date.now()}`,
      bondId: application.id,
      bondName: application.bondName,
      issuer: application.issuer,
      action: action, // 'approve' or 'reject'
      timestamp: new Date().toISOString(),
      facilityAgentNotes: notes,
      rejectionReason: rejectionReason,
      nominalAmount: application.nominalAmount,
      productType: application.productType,
      isin: extra.isin || application.isin || null,
      stockCode: extra.stockCode || application.stockCode || null,
    };

    try {
      const existingHistory = JSON.parse(
        localStorage.getItem("bondApprovalHistory") || "[]"
      );
      existingHistory.push(historyEntry);

      // Keep only last 100 entries to prevent localStorage from growing too large
      const limitedHistory = existingHistory.slice(-100);

      localStorage.setItem(
        "bondApprovalHistory",
        JSON.stringify(limitedHistory)
      );
      console.log("Approval history saved:", historyEntry);
    } catch (error) {
      console.error("Error saving approval history:", error);
    }
  };

  // Helper functions for bond approval
  const calculateMinSubscription = (totalAmount) => {
    // Calculate minimum subscription as 0.1% of total amount, with minimum of 1000
    return Math.max(Math.floor(totalAmount * 0.001), 1000);
  };

  // Reject application mutation
  const rejectMutation = useMutation({
    mutationFn: (applicationId) =>
      bondsAPI.rejectBond(applicationId, {
        rejectionReason: rejectionReason,
        notes: arrangerNotes,
      }),
    onSuccess: () => {
      // Save rejection to history
      saveApprovalHistory(
        "reject",
        selectedApplication,
        arrangerNotes,
        rejectionReason
      );

      queryClient.invalidateQueries(["bondApplications"]);
      setShowRejectModal(false);
      setSelectedApplication(null);
      setRejectionReason("");
      setArrangerNotes("");

      // Show success notification
      const bondName = selectedApplication?.bondName || "Bond";
      alert(
        `✅ Rejection Processed!\n\n${bondName} has been rejected and returned to the Issuer.\n\nThe Issuer will be notified with the rejection reason and can resubmit an updated application.`
      );
    },
    onError: (error) => {
      console.error("Rejection error:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to reject bond application. Please try again.";
      alert(`❌ Error: ${errorMessage}`);
    },
  });

  const handleApprove = (application) => {
    setSelectedApplication(application);
    setArrangerNotes("");
    setApprovalIsin(
      application.isin ? application.isin.trim().toUpperCase() : ""
    );
    setApprovalIsinError("");
    setApprovalStockCode(
      application.stockCode ? application.stockCode.trim().toUpperCase() : ""
    );
    setApprovalStockCodeError("");
    setShowApproveModal(true);
  };

  const confirmApprove = () => {
    if (!selectedApplication) return;

    const trimmedIsin = approvalIsin.trim().toUpperCase();
    const trimmedStockCode = approvalStockCode.trim().toUpperCase();

    let hasError = false;

    if (!trimmedIsin) {
      setApprovalIsinError("ISIN is required.");
      hasError = true;
    } else if (!ISIN_REGEX.test(trimmedIsin)) {
      setApprovalIsinError(
        "ISIN must be a 12-character code (e.g., XS0000000000)."
      );
      hasError = true;
    } else {
      setApprovalIsinError("");
    }

    if (!trimmedStockCode) {
      setApprovalStockCodeError("Stock Code is required.");
      hasError = true;
    } else if (!STOCK_CODE_REGEX.test(trimmedStockCode)) {
      setApprovalStockCodeError(
        "Stock Code must be an 8-character code (e.g., XS000000)."
      );
      hasError = true;
    } else {
      setApprovalStockCodeError("");
    }

    setApprovalIsin(trimmedIsin);
    setApprovalStockCode(trimmedStockCode);

    if (hasError) {
      return;
    }

    approveMutation.mutate({
      applicationId: selectedApplication.id,
      isin: trimmedIsin,
      stockCode: trimmedStockCode,
      notes: arrangerNotes,
    });
  };

  const handleReject = (application) => {
    setSelectedApplication(application);
    setShowRejectModal(true);
  };

  const confirmReject = () => {
    if (selectedApplication && rejectionReason.trim()) {
      rejectMutation.mutate(selectedApplication.id);
    }
  };

  // Document download handler
  const handleDownloadDocument = async (doc) => {
    try {
      console.log("Downloading document:", doc);
      const response = await filesAPI.downloadFile(doc.id);

      // Create blob URL and trigger download
      const blob = new Blob([response.data], {
        type: doc.mimeType || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = doc.originalName || doc.name || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("Document downloaded successfully");
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download document. Please try again.");
    }
  };

  // Document preview handler
  const handlePreviewDocument = async (doc) => {
    try {
      console.log("Previewing document:", doc);
      const response = await filesAPI.downloadFile(doc.id);

      // Create blob URL and open in new tab
      const blob = new Blob([response.data], {
        type: doc.mimeType || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const newWindow = window.open(url, "_blank");

      if (!newWindow) {
        alert("Please allow pop-ups to preview documents");
      }

      console.log("Document preview opened");
    } catch (error) {
      console.error("Preview failed:", error);
      alert("Failed to preview document. Please try again.");
    }
  };

  // Mock data for demonstration - simulating Issuer-created bonds awaiting FA approval
  const mockApplications = [
    {
      id: 1,
      bondName: "Malaysia Infrastructure Bond 2024",
      issuer: "Malaysia Infrastructure Corp",
      productType: "Conventional Bonds",
      nominalAmount: 500000000,
      submissionDate: "2024-12-15",
      status: "Pending Review",
      couponRate: 5.25,
      maturityDate: "2029-12-15",
      issuanceDate: "2024-12-15",
      currency: "MYR",
      documents: [
        { name: "Trust Deed", type: "Legal Documents", status: "Submitted" },
        {
          name: "Offering Circular",
          type: "Legal Documents",
          status: "Submitted",
        },
        {
          name: "Credit Rating Report",
          type: "Regulatory Filing",
          status: "Submitted",
        },
      ],
    },
    {
      id: 2,
      bondName: "Green Sukuk Infrastructure 2027",
      issuer: "Green Energy Holdings",
      productType: "Sukuk",
      nominalAmount: 100000000,
      submissionDate: "2024-01-20",
      status: "Pending Review",
      profitRate: 4.8,
      maturityDate: "2027-01-20",
      issuanceDate: "2024-01-20",
      currency: "MYR",
      documents: [
        {
          name: "Shariah Compliance Certificate",
          type: "Shariah Compliance Certificate",
          status: "Submitted",
        },
        {
          name: "Asset Valuation Report",
          type: "Valuation Report",
          status: "Submitted",
        },
        {
          name: "Sukuk Structure Agreement",
          type: "Legal Documents",
          status: "Submitted",
        },
      ],
    },
    {
      id: 5,
      bondName: "Smart City Development Bond",
      issuer: "Urban Development Corp",
      productType: "Conventional Bonds",
      nominalAmount: 300000000,
      submissionDate: "2024-12-18",
      status: "Pending Review",
      couponRate: 5.75,
      maturityDate: "2029-12-18",
      issuanceDate: "2024-12-25",
      currency: "MYR",
      documents: [
        {
          name: "Project Feasibility Study",
          type: "Legal Documents",
          status: "Submitted",
        },
        {
          name: "Environmental Impact Assessment",
          type: "Regulatory Filing",
          status: "Submitted",
        },
        {
          name: "Financial Projections",
          type: "Financial Documents",
          status: "Submitted",
        },
      ],
    },
    {
      id: 6,
      bondName: "Healthcare Expansion Sukuk",
      issuer: "Medical Services Holdings",
      productType: "Sukuk",
      nominalAmount: 180000000,
      submissionDate: "2024-12-19",
      status: "Pending Review",
      profitRate: 4.5,
      maturityDate: "2028-12-19",
      issuanceDate: "2024-12-30",
      currency: "MYR",
      documents: [
        {
          name: "Shariah Compliance Certificate",
          type: "Shariah Compliance Certificate",
          status: "Submitted",
        },
        {
          name: "Healthcare License",
          type: "Regulatory Filing",
          status: "Submitted",
        },
        {
          name: "Asset Ownership Certificate",
          type: "Legal Documents",
          status: "Submitted",
        },
      ],
    },
    {
      id: 3,
      bondName: "Tech Development Bond",
      issuer: "Tech Innovation Ltd",
      productType: "Conventional Bonds",
      nominalAmount: 150000000,
      submissionDate: "2024-12-13",
      status: "Approved",
      approvalDate: "2024-12-16",
      couponRate: 6.0,
      maturityDate: "2026-12-13",
      documents: [
        { name: "Business Plan", type: "Legal Documents", status: "Approved" },
        {
          name: "Financial Statements",
          type: "Regulatory Filing",
          status: "Approved",
        },
      ],
    },
    {
      id: 4,
      bondName: "Renewable Energy Sukuk",
      issuer: "Solar Power Malaysia",
      productType: "Sukuk",
      nominalAmount: 200000000,
      submissionDate: "2024-12-10",
      status: "Approved",
      approvalDate: "2024-12-12",
      maturityDate: "2028-12-10",
      documents: [
        {
          name: "Shariah Compliance Certificate",
          type: "Shariah Compliance Certificate",
          status: "Approved",
        },
        {
          name: "Environmental Impact Report",
          type: "Regulatory Filing",
          status: "Approved",
        },
      ],
    },
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      [BOND_STATUS.PENDING_REVIEW]: { color: "warning", icon: Clock },
      [BOND_STATUS.APPROVED]: { color: "success", icon: CheckCircle },
      [BOND_STATUS.REJECTED]: { color: "error", icon: XCircle },
    };

    const config = statusConfig[status] || { color: "secondary", icon: Clock };
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {typeof status === "string" ? status : "Unknown Status"}
      </span>
    );
  };

  // Use API data if available, fallback to mock data for demonstration
  const applicationsData =
    applications && applications.length > 0
      ? transformAPIData(applications)
      : applications !== undefined
      ? []
      : mockApplications; // Show empty if API returned empty, mock only if API not called yet

  const filteredApplications = applicationsData.filter((app) => {
    const safeProductType =
      typeof app.productType === "string" ? app.productType : "";
    const safeBondName = typeof app.bondName === "string" ? app.bondName : "";
    const safeIssuer = typeof app.issuer === "string" ? app.issuer : "";

    // Map filter value to display type for comparison
    const filterDisplayType = bondTypeFilter === "Fixed Notes" 
      ? "Conventional Bonds" 
      : bondTypeFilter;
    const matchesType =
      bondTypeFilter === "all" || safeProductType === filterDisplayType;
    const matchesSearch =
      searchTerm === "" ||
      safeBondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      safeIssuer.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesType && matchesSearch;
  });

  if (applicationsLoading) {
    return <LoadingSpinner size="lg" text="Loading applications..." />;
  }

  // // Ensure getDisplayName returns a string
  // const safeDisplayName = () => {
  //   try {
  //     const name = getDisplayName();
  //     return typeof name === 'string' ? name : 'User';
  //   } catch (error) {
  //     console.error('Error getting display name:', error);
  //     return 'User';
  //   }
  // };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Bond Applications
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Review and approve bond applications from issuers
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Filters and Search */}
        <div className="mb-6 bg-white rounded-lg border border-light-gray">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {/* Bond Type Filter */}
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Bond Type
                </label>
                <select
                  value={bondTypeFilter}
                  onChange={(e) => setBondTypeFilter(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Types</option>
                  <option value="Sukuk">Sukuk</option>
                  <option value="Fixed Notes">Conventional</option>
                </select>
              </div>

              {/* Search */}
              <div className="md:col-span-2">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Search Applications
                </label>
                <div className="relative">
                  <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by bond name or issuer..."
                    className="block w-full pl-10 border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Applications Table */}
        <div className="bg-white rounded-lg border border-light-gray">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Bond Applications ({filteredApplications.length})
              </h3>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">
                  {
                    filteredApplications.filter(
                      (app) => app.status === "Pending Review"
                    ).length
                  }{" "}
                  pending approval
                </span>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Bond Details</th>
                    <th className="table-header-cell">Issuer</th>
                    <th className="table-header-cell">Amount</th>
                    <th className="table-header-cell">Terms</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredApplications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            <SafeText fallback="Unknown Bond">
                              {application.bondName}
                            </SafeText>
                          </div>
                          <div className="text-sm text-gray-500">
                            <SafeText fallback="Unknown Type">
                              {application.productType}
                            </SafeText>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-2 text-gray-400" />
                          <div className="text-sm text-gray-900">
                            <SafeText fallback="Unknown Issuer">
                              {application.issuer}
                            </SafeText>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <SafeText fallback="Unknown Amount">
                            {formatCurrency(application.nominalAmount)}
                          </SafeText>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {application.couponRate
                            ? `${String(application.couponRate)}%`
                            : `${String(application.profitRate || 0)}%`}
                        </div>
                        <div className="text-sm text-gray-500">
                          Until{" "}
                          <SafeText fallback="Unknown Date">
                            {formatDate(application.maturityDate)}
                          </SafeText>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(application.status)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedApplication(application);
                              setShowDetailModal(true);
                            }}
                            className="text-primary-600 hover:text-primary-700 p-1"
                            title="View Details"
                            style={{ color: "#24295f" }}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {application.status === "Pending Review" && (
                            <>
                              <button
                                onClick={() => handleApprove(application)}
                                
                                className={`btn-primary btn-sm `}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleReject(application)}
                                
                                className={`btn-secondary btn-sm `}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredApplications.length === 0 && (
              <div className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No applications found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {applicationsLoading
                    ? "Loading..."
                    : applications !== undefined
                    ? "No bond applications match the current filter criteria."
                    : "No bond applications match the current filter criteria."}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Approval Modal */}
      {showApproveModal && selectedApplication && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-2xl">
            <div className="flex items-center mb-4">
              <CheckCircle className="w-6 h-6 mr-3 text-green-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Approve Bond Application
              </h3>
            </div>
            <p className="mb-4 text-sm text-gray-600">
              Confirm approval for{" "}
              <strong>{selectedApplication.bondName}</strong>? This action will
              approve the bond and move it to the subscription management page.
            </p>
            <div className="p-3 mb-4 border border-blue-200 rounded-lg bg-blue-50">
              <div className="flex items-start">
                <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-xs text-blue-800">
                  <p className="mb-1 font-medium">After approval:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Bond will be synced to subscription management page</li>
                    <li>Status will be updated to "Allocating Subscribers"</li>
                    <li>You can set subscription period and view details</li>
                    <li>Issuer will receive approval notification</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                ISIN <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={approvalIsin}
                maxLength={12}
                onChange={(e) => {
                  const sanitizedValue = e.target.value
                    .toUpperCase()
                    .replace(/\s/g, "");
                  setApprovalIsin(sanitizedValue);
                  if (approvalIsinError) {
                    setApprovalIsinError("");
                  }
                }}
                placeholder="e.g., XS0000000000"
                className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                  approvalIsinError ? "border-red-500" : "border-gray-300"
                }`}
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter the official 12-character ISIN assigned to this bond.
              </p>
              {approvalIsinError && (
                <p className="mt-1 text-xs text-red-600">{approvalIsinError}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Stock Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={approvalStockCode}
                maxLength={8}
                onChange={(e) => {
                  const sanitizedValue = e.target.value
                    .toUpperCase()
                    .replace(/\s/g, "");
                  setApprovalStockCode(sanitizedValue);
                  if (approvalStockCodeError) {
                    setApprovalStockCodeError("");
                  }
                }}
                placeholder="e.g., XS000000"
                className={`w-full px-3 py-2 text-sm border rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                  approvalStockCodeError ? "border-red-500" : "border-gray-300"
                }`}
              />
              <p className="mt-1 text-xs text-gray-500">
                Provide the exchange stock code (8 characters, e.g., XS000000).
              </p>
              {approvalStockCodeError && (
                <p className="mt-1 text-xs text-red-600">
                  {approvalStockCodeError}
                </p>
              )}
            </div>

            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Approval Notes (Optional)
              </label>
              <textarea
                value={arrangerNotes}
                onChange={(e) => setArrangerNotes(e.target.value)}
                rows={3}
                placeholder="Add any relevant notes or conditions..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex justify-end mt-6 space-x-3">
              <button
                onClick={() => {
                  setShowApproveModal(false);
                  setApprovalIsin("");
                  setApprovalIsinError("");
                  setApprovalStockCode("");
                  setApprovalStockCodeError("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                disabled={
                  approveMutation.isPending ||
                  !ISIN_REGEX.test(approvalIsin) ||
                  !STOCK_CODE_REGEX.test(approvalStockCode)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
              >
                {approveMutation.isPending
                  ? "Approving..."
                  : "Confirm Approval"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {showRejectModal && selectedApplication && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 mx-4 bg-white rounded-lg shadow-2xl">
            <div className="flex items-center mb-4">
              <XCircle className="w-6 h-6 mr-3 text-red-600" />
              <h3 className="text-lg font-medium text-gray-900">
                Reject Bond Application
              </h3>
            </div>
            <p className="mb-4 text-sm text-gray-600">
              Are you sure you want to reject{" "}
              <strong>{selectedApplication.bondName}</strong>?
            </p>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Rejection Reason *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="Please provide a reason for rejection..."
                required
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Internal Notes (Optional)
              </label>
              <textarea
                value={arrangerNotes}
                onChange={(e) => setArrangerNotes(e.target.value)}
                rows={2}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder="Add any internal notes..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={rejectMutation.isPending || !rejectionReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white transition-colors bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {rejectMutation.isPending
                  ? "Rejecting..."
                  : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bond Detail Modal */}
      {showDetailModal && selectedApplication && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <FileText className="w-6 h-6 mr-3 text-blue-600" />
                <h3 className="text-xl font-medium text-gray-900">
                  Bond Application Details
                </h3>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="pb-2 text-lg font-medium text-gray-900 border-b">
                  Basic Information
                </h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Bond Name
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedApplication.bondName}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Issuer
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedApplication.issuer}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Bond Type
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedApplication.productType}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nominal Amount
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatCurrency(selectedApplication.nominalAmount)}
                  </p>
                </div>
              </div>

              {/* Terms & Dates */}
              <div className="space-y-4">
                <h4 className="pb-2 text-lg font-medium text-gray-900 border-b">
                  Terms & Dates
                </h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {selectedApplication.productType === "Sukuk"
                      ? "Profit Rate"
                      : "Coupon Rate"}
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedApplication.couponRate
                      ? `${selectedApplication.couponRate}%`
                      : `${selectedApplication.profitRate}%`}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Issuance Date
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedApplication.issuanceDate)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Maturity Date
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedApplication.maturityDate)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Submission Date
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedApplication.submissionDate)}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <div className="mt-1">
                    {getStatusBadge(selectedApplication.status)}
                  </div>
                </div>
              </div>
            </div>

            {/* Documents */}
            {documentsData &&
            documentsData.filter(isIssuerDocument).length > 0 ? (
              <div className="mb-6">
                <h4 className="pb-2 mb-4 text-lg font-medium text-gray-900 border-b">
                  Issuer Submitted Documents
                </h4>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {documentsData.filter(isIssuerDocument).map((doc, index) => (
                    <div
                      key={`${doc.id}-${index}`}
                      className="p-3 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 flex-shrink-0">
                            <FileText className="w-4 h-4 text-blue-600" />
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            {getDocumentCategoryLabel(doc)}
                          </span>
                        </div>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${
                            doc.status === "Submitted"
                              ? "bg-blue-100 text-blue-800"
                              : doc.status === "Approved"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {doc.status}
                        </span>
                      </div>
                      <div className="pl-10">
                        <p
                          className="text-xs text-gray-600 truncate"
                          title={doc.name || doc.originalName}
                        >
                          {doc.name || doc.originalName}
                        </p>
                        {doc.size && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            {(doc.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-100">
                        <button
                          onClick={() => handlePreviewDocument(doc)}
                          className="flex items-center text-xs text-blue-600 hover:text-blue-800 transition-colors"
                          title="Preview document"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />
                          Preview
                        </button>
                        <button
                          onClick={() => handleDownloadDocument(doc)}
                          className="flex items-center text-xs text-green-600 hover:text-green-800 transition-colors"
                          title="Download document"
                        >
                          <Download className="w-3.5 h-3.5 mr-1" />
                          Download
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mb-6">
                <h4 className="pb-2 mb-4 text-lg font-medium text-gray-900 border-b">
                  Issuer Submitted Documents
                </h4>
                <div className="py-8 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">
                    No issuer documents found for this bond application.
                  </p>
                  <p className="text-xs">
                    Documents may still be uploading or were not submitted by
                    the issuer.
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {selectedApplication.status === "Pending Review" && (
              <div className="flex justify-end pt-4 space-x-3 border-t">
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleApprove(selectedApplication);
                  }}
                  
                  className={`px-4 py-2 text-sm font-medium text-white transition-colors bg-green-600 rounded-md hover:bg-green-700 `}
                >
                  Approve
                </button>
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleReject(selectedApplication);
                  }}
                  
                  className={`px-4 py-2 text-sm font-medium text-white transition-colors bg-red-600 rounded-md hover:bg-red-700 `}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BondApplications;
